import * as angular from 'angular';
import * as constants from '../../common/constants';

import { IRequestService, IUploadService } from '../../services';
import { TagDefinitionDTO, TagImportResultDto } from '../../models/DTO';
import { Alias, ImportedTagDefinition, Range, TagDefinition } from '../../models';

export interface ITagDefinitionService {
    count(): angular.IPromise<number>;
    getAll(): angular.IPromise<TagDefinitionDTO[]>;
    getAll(force: boolean): angular.IPromise<TagDefinitionDTO[]>;
    getByConnectionPointGuid(guid: string): angular.IPromise<TagDefinitionDTO[]>;
    getByGuid(guid: string): angular.IPromise<TagDefinitionDTO>;
    getByGuids(guids: string[]): angular.IPromise<TagDefinitionDTO[]>;
    getUnused(): angular.IPromise<TagDefinitionDTO[]>;
    getMinMax(guid: string): angular.IPromise<Range>;
    add(tagDefinition: TagDefinition): angular.IPromise<TagDefinition>;
    update(tagDefinitions: TagDefinition[]): angular.IPromise<TagDefinition[]>;
    upload(file: File, progressCalback?): angular.IPromise<TagImportResultDto>;
    search(parameters: any): angular.IPromise<ImportedTagDefinition[]>;
    import(tagImport: ImportedTagDefinition): angular.IPromise<number[]>;
    importList(tagsImportlist: ImportedTagDefinition[], tempAliasList: Alias[]): angular.IPromise<number[]>;
    findUnknownUnits(units: string[]): angular.IPromise<string[]>;
    deleteTag(guid): angular.IPromise<any>;
    deleteTagList(guidList: string[]): angular.IPromise<TagDefinition[]>;
    cleanDeleteCache(url?: string): void;
}

export class TagDefinitionService implements ITagDefinitionService {
    static serviceId = 'tagDefinitionService';

    private _controllerUrl: string;

    static $inject = [
        '$q',
        'requestService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _upload: IUploadService
    ) {
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'tagDefinition/';
        }
        return this._controllerUrl;
    }

    count(): angular.IPromise<number> {
        const url = this.controllerUrl() + 'count';
        return this._requestService.get(url);
    }

    getAll(): angular.IPromise<any>;
    getAll(force: boolean): angular.IPromise<any>;
    getAll(force?: boolean): angular.IPromise<any> {
        const url = this.controllerUrl();
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url, undefined, true);
    }

    getByGuid(guid: string): angular.IPromise<TagDefinitionDTO> {
        const url = this.controllerUrl() + guid;
        return this._requestService.get(url, undefined, true);
    }

    getByGuids(guids: string[]): angular.IPromise<TagDefinitionDTO[]> {
        const url = this.controllerUrl() + 'getList';
        const data = guids;
        return this._requestService.post(url, data, undefined, true);
    }

    getUnused(): angular.IPromise<TagDefinitionDTO[]> {
        const url = this.controllerUrl() + 'getUnused';
        return this._requestService.get(url);
    }

    update(tagDefinitionList: any[]) {
        const url = this.controllerUrl();
        this._requestService.cleanAllCache();
        this.cleanDeleteCache();
        this._requestService.cleanAllCache();
        this._requestService.cleanCacheAtV1('viewModel/guidList');
        tagDefinitionList.forEach(t => {
            this._requestService.cleanCache(`${this.controllerUrl()}${t.guid}`);
            this.makeValidEmptyGuid(t);
            t.lagSpan = undefined;
        });

        this._requestService.cleanCache(url);
        this._requestService.cleanCacheAtV1('group');
        this._requestService.cleanCacheAtV1('viewModel/all');

        return this._requestService.post(url, tagDefinitionList, undefined, true);
    }

    upload(file: File): angular.IPromise<TagImportResultDto> {
        const url = this.controllerUrl() + 'upload/';
        return this._upload
            .uploadFile<TagImportResultDto>(url, file)
            .then(response => {
                const data: TagImportResultDto = response;
                this.cleanDependentCache();
                data.updated.forEach(t => {
                    this._requestService.cleanCache(`${this.controllerUrl()}${t.guid}`);
                    this.makeValidEmptyGuid(t);
                    t.lagSpan = undefined;
                });
                return data;
            });
    }

    getMinMax(guid: string): angular.IPromise<Range> {
        const url = this.controllerUrl() + 'minMax/' + guid;
        return this._requestService.get(url, undefined, true);
    }

    getByConnectionPointGuid(guid: string): angular.IPromise<TagDefinitionDTO[]> {
        const url = this.controllerUrl() + 'connectionPoint/' + guid;
        return this._requestService.get(url, undefined, true);
    }

    add(tagDefinition: TagDefinition): angular.IPromise<TagDefinition> {
        const url = this.controllerUrl() + 'add';
        this._requestService.cleanCache(`${this.controllerUrl()}${tagDefinition.guid}`);
        this.cleanDeleteCache();
        this.makeValidEmptyGuid(tagDefinition);
        return this._requestService.post(url, tagDefinition, undefined, true);
    }

    search(parameters: any): angular.IPromise<ImportedTagDefinition[]> {
        const url = `${this.controllerUrl()}search`;
        return this._requestService.post(url, parameters);
    }

    import(tagImport: ImportedTagDefinition): angular.IPromise<number[]> {
        const url = `${this.controllerUrl()}import`;
        this.cleanDeleteCache();
        return this._requestService.post(url, tagImport, undefined, true);
    }

    importList(tagsImportlist: ImportedTagDefinition[], tempAliasList: Alias[]): angular.IPromise<number[]> {
        if (tagsImportlist.length > 0) {
            const argument = {
                importedTagDefinitionList: tagsImportlist,
                tempAliasList: tempAliasList
            };
            const url = `${this.controllerUrl()}ImportListWithAlias`;
            this.cleanDeleteCache();
            return this._requestService.post(url, argument, undefined, true);
        } else {
            return this._$q.when([]);
        }
    }

    findUnknownUnits(units: string[]): angular.IPromise<string[]> {
        const url = `${this.controllerUrl()}FindUnknownUnit`;
        this._requestService.cleanCache(this.controllerUrl());
        return this._requestService.post(url, units);
    }

    deleteTag(guid: string): angular.IPromise<void> {
        const url = `${this.controllerUrl()}delete/${guid}`;
        return this._requestService
            .post(url)
            .then(() => this.cleanDeleteCache(url));
    }

    deleteTagList(guidList: string[]): angular.IPromise<TagDefinition[]> {
        const url = `${this.controllerUrl()}deleteList`;
        return this._requestService
            .post(url, guidList)
            .then(data => {
                this.cleanDeleteCache();
                return data;
            });
    }

    public cleanDeleteCache(url?: string) {
        this._requestService.cleanCache(this.controllerUrl());
        this._requestService.cleanCache(this.controllerUrl() + 'getUnused');
        if (url) {
            this._requestService.cleanCache(url);
        }
        this.cleanDependentCache();
    }

    private cleanDependentCache() {
        this._requestService.cleanCacheAtV1('tagDefinition/');
        this._requestService.cleanCacheAtV1('viewModel/guidList');
        this._requestService.cleanCacheAtV1('viewModel/all');
    }

    private makeValidEmptyGuid(tag: TagDefinition) {
        if (tag.acquisitionPointGuid === null) {
            tag.acquisitionPointGuid = constants.guidEmpty;
        }
        if (tag.qualifierGuid === null) {
            tag.qualifierGuid = constants.guidEmpty;
        }
        if (tag.unitGuid === null) {
            tag.unitGuid = constants.guidEmpty;
        }
        if (tag.variableSubtypeGuid === null) {
            tag.variableSubtypeGuid = constants.guidEmpty;
        }
        if (tag.variableTypeGuid === null) {
            tag.variableTypeGuid = constants.guidEmpty;
        }
    }
}